import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Media from 'react-media';
import { useLocation, Link } from 'react-router-dom';
import { Layout } from '../../contexts/LayoutContext';
import CreateAccountForm from '../CreateAccount/Form';
import mediaQueries from '../../utils/mediaQueries';
import GlobalTheme from '../../styled/GlobalTheme';
import FacebookButton from '../auth/components/FacebookButton';
import roles from '../../utils/constants/roles';
import PromoCodeContext from '../../contexts/PromoCodeContext';
import { WebViewContext } from '../../contexts/WebViewContext';
import AppleButton from '../auth/components/AppleButton';
import { QueryStringContext } from '../../contexts/QueryStringContext';
import { QUERY_KEYS, QUERY_VALUES } from '../../utils/constants/settings';
import Pipers from '../../components/Pipers/Pipers';

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 740px;
  ${GlobalTheme.breakpoints.down('lg')} {
    margin: 0 auto;
  }
`;

const LegalAccept = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  font-size: 9px;
  color: ${(props) => props.theme.colors.text02};
  text-align: center;
  padding: 10px;
  @media ${mediaQueries.mdUp} {
    font-size: 12px;
    padding: 26px 18px;
  }
  @media (min-width: 810px) {
    align-self: end;
  }
`;

const LegalLink = styled.a`
  color: ${(props) => props.theme.colors.lightPurple};
  text-decoration: underline;
  font-weight: normal;
`;

const Container = styled.div`
  background: linear-gradient(180deg, #ffdd00 0%, #ffcf24 100%);
  min-height: 100vh;
  overflow-y: scroll;
  padding-top: 58px;
  @media ${mediaQueries.mdUp} {
    padding-top: 50px;
  }
  @media (min-width: 810px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: max-content 1fr max-content;
    justify-content: center;
    align-items: center;
    padding-top: 48px;
  }
  @media (min-width: 820px) {
    padding-top: 76px;
  }
  @media (min-width: 1024px) {
    padding-top: 116px;
  }
  @media (max-width: 1366px) and (orientation: landscape) {
    padding-top: 60px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 28px;
  @media ${mediaQueries.mdUp} {
    margin-bottom: 24px;
  }
  @media (min-width: 810px) {
    margin-bottom: 0;
  }
`;
const PiperContainer = styled.div`
  max-width: 154px;
  max-height: 33px;
  @media ${mediaQueries.mdUp} {
    max-width: 340px;
    max-height: 73px;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-gap: ${(props) => props.theme.space * 4}px;
  grid-template-columns: 1fr;
  padding-right: 24px;
  padding-left: 24px;
  padding-left: max(24px, env(safe-area-inset-left));
  padding-right: max(24px, env(safe-area-inset-right));
  @media ${mediaQueries.mdUp} {
    max-width: 390px;
    margin: 0 auto;
    padding-right: 0;
    padding-left: 0;
  }
  @media (min-width: 810px) {
    margin-top: 24px;
    align-self: start;
  }
  @media (min-width: 820px) {
    margin-top: 48px;
  }
  @media (min-width: 1024px) {
    margin-top: 50px;
  }
  @media (max-width: 1366px) and (orientation: landscape) {
    margin-top: 24px;
  }
`;

const SectionHeader = styled.div`
  font-size: 30px;
  font-weight: 600;
  margin: 0 auto;
  width: max-content;
  @media ${mediaQueries.mdUp} {
    font-size: 36px;
  }
`;

const SectionTitle = styled.div`
  text-align: left;
  width: 100%;
  ${(props) => props.bold && `
    font-weight: 600;
  `}
  @media ${mediaQueries.mdUp} {
    margin-bottom: 10px;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  margin-top: 10px;
  @media ${mediaQueries.mdUp} {
    font-size: 14px;
    line-height: 18px;
    margin-top: 20px;
  }
`;

const SectionRequiredText = styled.div`
  text-align: left;
  width: 100%;
  font-size: 9px;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.colors.lightPurple};
`;

const ButtonsWrapperSN = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ButtonContainerSN = styled.div`
  margin: 0;
  text-align: center;
  &:nth-child(1) {
    margin-right: 8px;
  }
  @media ${mediaQueries.mdUp} {
    width: 166px;
    &:nth-child(1) {
      margin-right: 16px;
    }
  }
`;
const HaveAccountContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
  @media ${mediaQueries.mdUp} {
    margin-top: 14px;
    margin-bottom: 30px;
  }
`;
const HaveAccountText = styled(Link)`
  color: ${({ theme }) => theme.colors.text02};
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
`;

const TextSignupWith = styled.div`
  font-size: 16px;
  font-weight: 600;
  width: 100%;
  text-align: center;
  text-transform: capitalize;
  &::after {
    content: ':';
  }
`;

const TextTrial = styled.span`
  color: ${({ theme }) => theme.colors.lightPurple};
`;
const TextBold = styled.span`
  font-weight: 600;
`;
const LinkText = styled(Link)`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grayMediaLibrary};
  font-weight: 600;
  text-decoration: underline;
`;

const CreateAccount = () => {
  const intl = useIntl();
  const location = useLocation();
  const { PromoCode } = useContext(PromoCodeContext);
  const { getQueryStringVal } = useContext(QueryStringContext);
  const [planSelected, setPlanSelected] = useState('P');
  const typeOfTrial = getQueryStringVal(QUERY_KEYS.tt);
  const { isApp, isAppIOS } = useContext(WebViewContext);
  const isOptIn = typeOfTrial === QUERY_VALUES.tt;

  useEffect(() => {
    setPlanSelected((location.state && location.state.plan) || 'P');
  }, []);

  return (
    <Media queries={mediaQueries}>
      {(matches) => (
        <Layout
          title={intl.formatMessage({
            id: 'onboarding.createAccount.title',
            defaultMessage: 'Create your account',
          })}
          description={intl.formatMessage({
            id: 'onboarding.createAccount.description',
            defaultMessage: 'Start your 30-day free trial.',
          })}
          disableHeader
          accessOnTrialEnds
        >
          <Container>
            <div>
              <HeaderContainer>
                <PiperContainer>
                  <Pipers.PiperLogo />
                </PiperContainer>
              </HeaderContainer>
              <MainGrid>
                <ContentSection>
                  <SectionHeader opacity={planSelected ? '1' : '0.25'}>
                    <FormattedMessage
                      id="onboarding.creteAccount.heading"
                      description="Create account heading"
                      defaultMessage="Create your account"
                    />
                  </SectionHeader>
                  <HelpText>
                    <FormattedMessage
                      id="signup.createAccount.app.helpText1"
                      defaultMessage="<TextBold>You are creating an account in the</TextBold> <r>PRO plan {days} day-free trial.</r> <LinkText>See plan features here.</LinkText>. You will not be charged during your trial period."
                      values={{
                        r: (...chunks) => <TextTrial>{chunks}</TextTrial>,
                        days: PromoCode.plans[0].trialDuration,
                        TextBold: (...chunks) => <TextBold>{chunks}</TextBold>,
                        b: (...chunks) => <b>{chunks}</b>,
                        LinkText: (...chunks) => (
                          <LinkText to="/pricing">{chunks}</LinkText>
                        ),
                      }}
                    />
                  </HelpText>
                  <SectionRequiredText>
                    <FormattedMessage
                      id="signup.createAccount.text.required"
                      defaultMessage="* Required"
                    />
                  </SectionRequiredText>
                  <CreateAccountForm
                    wantsFree={roles.PRO}
                    planSelected={planSelected}
                    hideHolderBottom
                    hideMobileInput
                  />

                  <SectionTitle bold>
                    <TextSignupWith>
                      <FormattedMessage id="k.or" defaultMessage="Or" />
                    </TextSignupWith>
                  </SectionTitle>
                  <ButtonsWrapperSN>
                    <ButtonContainerSN>
                      <FacebookButton
                        PromoCode={PromoCode}
                        wantsFree={false}
                        disabled={!planSelected}
                        buttonType="signupapp"
                        isApp
                        isAndroid={isApp && !isAppIOS}
                      />
                    </ButtonContainerSN>
                    <ButtonContainerSN>
                      <AppleButton
                        PromoCode={PromoCode}
                        wantsFree={false}
                        buttonType="signup"
                      />
                    </ButtonContainerSN>
                  </ButtonsWrapperSN>
                  <HaveAccountContainer>
                    <HaveAccountText to="/login-app">
                      <FormattedMessage
                        id="signup.createAccount.haveAccount.text"
                        defaultMessage="I HAVE AN ACCOUNT"
                      />
                    </HaveAccountText>
                  </HaveAccountContainer>
                </ContentSection>
              </MainGrid>
            </div>
            <LegalAccept>
              <FormattedMessage
                id="onboarding.creteAccount.acceptAllTerms"
                defaultMessage="By registering, you are accepting: <terms>Terms of Service</terms>, <cookies>Cookies Policy</cookies>, <privacy>Privacy Policy</privacy>, <caPrivacy>CA Privacy Rights</caPrivacy> and <doNotSell>Do Not Sell My Information</doNotSell> of Social Piper, and you will be automatically registered to receive emails about new Social Piper products, best practices or special offers, until you opt out."
                values={{
                  terms: (...chunks) => (
                    <LegalLink
                      target="_blank"
                      href="https://legal.socialpiper.com/social-piper-terms-of-service-2020-07-31/"
                    >
                      {chunks}
                    </LegalLink>
                  ),
                  cookies: (...chunks) => (
                    <LegalLink
                      target="_blank"
                      href="https://legal.socialpiper.com/social-piper-cookie-policy-2020-08-03/"
                    >
                      {chunks}
                    </LegalLink>
                  ),
                  privacy: (...chunks) => (
                    <LegalLink
                      target="_blank"
                      href="https://legal.socialpiper.com/social-piper-privacy-policy-2020-08-03/"
                    >
                      {chunks}
                    </LegalLink>
                  ),
                  caPrivacy: (...chunks) => (
                    <LegalLink
                      target="_blank"
                      href="https://legal.socialpiper.com/social-piper-ca-privacy-policy-2020-08-03/"
                    >
                      {chunks}
                    </LegalLink>
                  ),
                  doNotSell: (...chunks) => (
                    <LegalLink
                      target="_blank"
                      href="https://legal.socialpiper.com/social-piper-do-not-sell-my-personal-information-2020-08-03/"
                    >
                      {chunks}
                    </LegalLink>
                  ),
                }}
              />
            </LegalAccept>
          </Container>
        </Layout>
      )}
    </Media>
  );
};

export default CreateAccount;
